import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { RolePermissionsSelector } from "./RolePermissionsSelector";
import { logCrud } from "@/services/activities";

const roleSchema = z.object({
  name: z.string().min(3, "Nome deve ter no mínimo 3 caracteres"),
  description: z.string().optional(),
  scope: z.enum(["global", "tenant"]),
});

type RoleFormData = z.infer<typeof roleSchema>;

interface Role {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  scope: string;
}

interface Permission {
  id: string;
  name: string;
  module: string;
  action: string;
  slug: string;
}

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  role?: Role | null;
}

export function RoleModal({ isOpen, onClose, role }: RoleModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!role;
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);

  const form = useForm<RoleFormData>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: "",
      description: "",
      scope: "tenant",
    },
  });

  // Buscar todas as permissions
  const { data: permissions = [] } = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("permissions" as any)
        .select("*")
        .order("module", { ascending: true })
        .order("action", { ascending: true }) as any;

      if (error) throw error;
      return data as Permission[];
    },
  });

  // Buscar permissions do role atual (se editando)
  useQuery({
    queryKey: ["role-permissions", role?.id],
    queryFn: async () => {
      if (!role?.id) return [];

      const { data, error } = await supabase
        .from("role_permissions_junction" as any)
        .select("permission_id")
        .eq("role_id", role.id) as any;

      if (error) throw error;
      
      const permissionIds = (data || []).map((rp: any) => rp.permission_id);
      setSelectedPermissionIds(permissionIds);
      
      return data;
    },
    enabled: isEditing && isOpen,
  });

  useEffect(() => {
    if (role) {
      form.reset({
        name: role.name,
        description: role.description || "",
        scope: role.scope as "global" | "tenant",
      });
    } else {
      form.reset({
        name: "",
        description: "",
        scope: "tenant",
      });
      setSelectedPermissionIds([]);
    }
  }, [role, form]);

  const mutation = useMutation({
    mutationFn: async (data: RoleFormData) => {
      const slug = data.name
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

      if (isEditing) {
        // Atualizar role
        const { error: roleError } = await supabase
          .from("roles" as any)
          .update({
            name: data.name,
            slug,
            description: data.description || null,
            scope: data.scope,
          } as any)
          .eq("id", role.id);

        if (roleError) throw roleError;

        // Atualizar permissions (deletar todas e reinserir)
        const { error: deleteError } = await supabase
          .from("role_permissions_junction" as any)
          .delete()
          .eq("role_id", role.id);

        if (deleteError) throw deleteError;

        if (selectedPermissionIds.length > 0) {
          const { error: insertError } = await supabase
            .from("role_permissions_junction" as any)
            .insert(
              selectedPermissionIds.map((permId) => ({
                role_id: role.id,
                permission_id: permId,
              }))
            ) as any;

          if (insertError) throw insertError;
        }
      } else {
        // Criar role
        const { data: newRole, error: roleError } = await supabase
          .from("roles" as any)
          .insert({
            name: data.name,
            slug,
            description: data.description || null,
            scope: data.scope,
          } as any)
          .select()
          .single() as { data: any; error: any };

        if (roleError) throw roleError;

        // Inserir permissions
        if (selectedPermissionIds.length > 0 && newRole?.id) {
          const { error: insertError } = await supabase
            .from("role_permissions_junction" as any)
            .insert(
              selectedPermissionIds.map((permId) => ({
                role_id: newRole.id,
                permission_id: permId,
              }))
            ) as any;

          if (insertError) throw insertError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role-permissions"] });
      toast.success(isEditing ? "Perfil atualizado!" : "Perfil criado!");
      const name = form.getValues("name");
      if (isEditing && role?.id) {
        void logCrud({ entitySlug: "entity:roles", operationSlug: "update", entityId: role.id, entityName: name, screenSlug: "perfis", before: (role as any), after: { name, permission_ids: selectedPermissionIds } });
      } else {
        void logCrud({ entitySlug: "entity:roles", operationSlug: "create", entityName: name, screenSlug: "perfis" });
      }
      form.reset();
      setSelectedPermissionIds([]);
      onClose();
    },
    onError: (error: any) => {
      console.error("Erro ao salvar perfil:", error);
      toast.error(
        error.message?.includes("duplicate")
          ? "Já existe um perfil com este nome"
          : "Erro ao salvar perfil"
      );
    },
  });

  const onSubmit = (data: RoleFormData) => {
    if (selectedPermissionIds.length === 0) {
      toast.error("Selecione pelo menos uma permissão");
      return;
    }
    mutation.mutate(data);
  };

  const currentSlug = form.watch("name")
    ? form.watch("name")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
    : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Perfil" : "Novo Perfil"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Atualize os dados do perfil e suas permissões"
              : "Crie um novo perfil de acesso com suas permissões"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Perfil</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Gerente de Vendas" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {currentSlug && (
              <div className="rounded-md bg-muted p-3">
                <p className="text-sm text-muted-foreground mb-1">Slug gerado:</p>
                <code className="text-sm font-mono">{currentSlug}</code>
              </div>
            )}

            <FormField
              control={form.control}
              name="scope"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Escopo</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o escopo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="tenant">Tenant (padrão)</SelectItem>
                      <SelectItem value="global">Global (Admin SaaS)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Tenant: perfil válido apenas dentro de um tenant. Global: perfil válido em todo o sistema.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição (Opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva o propósito deste perfil..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>
                Permissões
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({selectedPermissionIds.length} selecionadas)
                </span>
              </FormLabel>
              <RolePermissionsSelector
                permissions={permissions}
                selectedPermissionIds={selectedPermissionIds}
                onPermissionsChange={setSelectedPermissionIds}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
